import apiClient, { withAuth } from './api';

/**
 * PIN Verification Service
 * Handles two-way verification PINs for secure customer support
 * Follows the same pattern as other services in the project
 */
export const pinVerificationService = {
  /**
   * Get current PIN status for the authenticated user
   * @param {string} token - Auth token
   * @returns {Promise} - Response with PIN status and data
   */
  getPinStatus: (token) => {
    console.log('Fetching PIN status for user');
    return apiClient.get('/api/verification/pins/status/', withAuth(token))
      .then(response => {
        console.log('PIN status retrieved successfully:', {
          has_pins: response.data.has_pins,
          pins_valid: response.data.pins_valid,
          expires_at: response.data.expires_at
        });
        return response;
      })
      .catch(error => {
        console.error('Error fetching PIN status:', error);
        throw error;
      });
  },

  /**
   * Generate new verification PINs for the user
   * @param {string} token - Auth token
   * @returns {Promise} - Response with generated PIN data
   */
  generatePins: (token) => {
    console.log('Generating new verification PINs');
    return apiClient.post('/api/verification/pins/generate/', {}, withAuth(token))
      .then(response => {
        console.log('PINs generated successfully:', {
          has_pins: !!response.data.member_pin && !!response.data.agent_pin,
          expires_at: response.data.expires_at,
          generated_at: response.data.generated_at
        });
        return response;
      })
      .catch(error => {
        console.error('Error generating PINs:', error);
        throw error;
      });
  },

  /**
   * Verify both member and agent PINs
   * @param {Object} data - PIN verification data
   * @param {string} data.member_pin - 6-digit member PIN
   * @param {string} data.agent_pin - 6-digit agent PIN
   * @param {string} token - Auth token
   * @returns {Promise} - Response with verification result
   */
  verifyPins: (data, token) => {
    console.log('Verifying PIN pair');
    
    // Validate input before sending
    if (!data.member_pin || !data.agent_pin) {
      return Promise.reject({
        isApiError: true,
        status: 400,
        message: 'Both member PIN and agent PIN are required',
        friendlyMessage: 'Please enter both PINs to verify'
      });
    }

    if (data.member_pin.length !== 6 || data.agent_pin.length !== 6) {
      return Promise.reject({
        isApiError: true,
        status: 400,
        message: 'PINs must be exactly 6 digits',
        friendlyMessage: 'Each PIN must be exactly 6 digits'
      });
    }

    if (!/^\d+$/.test(data.member_pin) || !/^\d+$/.test(data.agent_pin)) {
      return Promise.reject({
        isApiError: true,
        status: 400,
        message: 'PINs must contain only digits',
        friendlyMessage: 'PINs can only contain numbers'
      });
    }

    return apiClient.post('/api/verification/pins/verify/', data, withAuth(token))
      .then(response => {
        console.log('PIN verification completed:', {
          success: response.data.success,
          verified_at: response.data.verified_at
        });
        return response;
      })
      .catch(error => {
        console.error('Error verifying PINs:', error);
        throw error;
      });
  },

  /**
   * Regenerate PINs (clears existing and creates new ones)
   * @param {string} token - Auth token
   * @returns {Promise} - Response with new PIN data
   */
  regeneratePins: (token) => {
    console.log('Regenerating verification PINs');
    return apiClient.post('/api/verification/pins/regenerate/', {}, withAuth(token))
      .then(response => {
        console.log('PINs regenerated successfully:', {
          has_pins: !!response.data.member_pin && !!response.data.agent_pin,
          expires_at: response.data.expires_at,
          generated_at: response.data.generated_at
        });
        return response;
      })
      .catch(error => {
        console.error('Error regenerating PINs:', error);
        throw error;
      });
  },

  /**
   * Debug endpoint to check raw PIN data (for development/testing)
   * @param {string} token - Auth token
   * @returns {Promise} - Response with debug PIN information
   */
  debugPinData: (token) => {
    console.log('Fetching debug PIN data');
    return apiClient.get('/api/verification/pins/debug/', withAuth(token))
      .then(response => {
        console.log('Debug PIN data retrieved:', response.data);
        return response;
      })
      .catch(error => {
        console.error('Error fetching debug PIN data:', error);
        throw error;
      });
  },

  /**
   * Helper function to format PIN status for UI display
   * @param {Object} pinData - PIN data from API
   * @returns {Object} - Formatted status information
   */
  formatPinStatus: (pinData) => {
    if (!pinData) {
      return {
        status: 'no_data',
        statusText: 'No PIN data',
        statusColor: 'gray',
        canGenerate: true,
        canRegenerate: false
      };
    }

    if (!pinData.has_pins) {
      return {
        status: 'no_pins',
        statusText: 'No PINs Generated',
        statusColor: 'red',
        canGenerate: true,
        canRegenerate: false
      };
    }

    if (!pinData.pins_valid) {
      return {
        status: 'expired',
        statusText: 'PINs Expired',
        statusColor: 'orange',
        canGenerate: false,
        canRegenerate: true
      };
    }

    // Check if expiring soon (within 2 hours)
    if (pinData.expires_at) {
      const expiryTime = new Date(pinData.expires_at);
      const now = new Date();
      const hoursUntilExpiry = (expiryTime - now) / (1000 * 60 * 60);
      
      if (hoursUntilExpiry < 2 && hoursUntilExpiry > 0) {
        return {
          status: 'expiring_soon',
          statusText: 'Expiring Soon',
          statusColor: 'orange',
          canGenerate: false,
          canRegenerate: true,
          timeRemaining: Math.max(0, Math.round(hoursUntilExpiry * 60)) // minutes
        };
      }
    }

    return {
      status: 'active',
      statusText: 'Active PINs',
      statusColor: 'green',
      canGenerate: false,
      canRegenerate: true
    };
  },

  /**
   * Helper function to format time remaining until expiry
   * @param {string} expiryDateString - ISO date string
   * @returns {string} - Human-readable time remaining
   */
  formatTimeRemaining: (expiryDateString) => {
    if (!expiryDateString) return null;
    
    const expiryTime = new Date(expiryDateString);
    const now = new Date();
    const diffMs = expiryTime - now;
    
    if (diffMs <= 0) return 'Expired';
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  },

  /**
   * Helper function to check if PINs need attention (expired or expiring soon)
   * @param {Object} pinData - PIN data from API
   * @returns {boolean} - True if PINs need attention
   */
  needsAttention: (pinData) => {
    const status = this.formatPinStatus(pinData);
    return ['expired', 'expiring_soon', 'no_pins'].includes(status.status);
  },

  /**
   * Helper function to get status chip color for Material-UI
   * @param {string} statusColor - Color from formatPinStatus
   * @returns {string} - Material-UI chip color
   */
  getChipColor: (statusColor) => {
    switch (statusColor) {
      case 'green':
        return 'success';
      case 'orange':
        return 'warning';
      case 'red':
        return 'error';
      default:
        return 'default';
    }
  },

  /**
   * Helper function to format generated/expiry dates for display
   * @param {string} dateString - ISO date string
   * @returns {string} - Formatted date string
   */
  formatDateTime: (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  },

  /**
   * Helper function to copy text to clipboard with error handling
   * @param {string} text - Text to copy
   * @param {string} type - Type of PIN (for logging)
   * @returns {Promise<boolean>} - Success status
   */
  copyToClipboard: async (text, type = 'PIN') => {
    try {
      await navigator.clipboard.writeText(text);
      console.log(`${type} copied to clipboard successfully`);
      return true;
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const result = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (result) {
          console.log(`${type} copied to clipboard using fallback method`);
          return true;
        }
      } catch (fallbackError) {
        console.error('Fallback copy method also failed:', fallbackError);
      }
      
      return false;
    }
  },

  /**
   * Helper function to validate PIN format
   * @param {string} pin - PIN to validate
   * @returns {Object} - Validation result
   */
  validatePin: (pin) => {
    if (!pin) {
      return {
        isValid: false,
        error: 'PIN is required'
      };
    }

    if (typeof pin !== 'string') {
      return {
        isValid: false,
        error: 'PIN must be a string'
      };
    }

    if (pin.length !== 6) {
      return {
        isValid: false,
        error: 'PIN must be exactly 6 digits'
      };
    }

    if (!/^\d+$/.test(pin)) {
      return {
        isValid: false,
        error: 'PIN must contain only numbers'
      };
    }

    return {
      isValid: true,
      error: null
    };
  },

  /**
   * Helper function to validate both member and agent PINs
   * @param {string} memberPin - Member PIN
   * @param {string} agentPin - Agent PIN
   * @returns {Object} - Validation result
   */
  validatePinPair: (memberPin, agentPin) => {
    const memberValidation = this.validatePin(memberPin);
    if (!memberValidation.isValid) {
      return {
        isValid: false,
        error: `Member PIN: ${memberValidation.error}`
      };
    }

    const agentValidation = this.validatePin(agentPin);
    if (!agentValidation.isValid) {
      return {
        isValid: false,
        error: `Agent PIN: ${agentValidation.error}`
      };
    }

    return {
      isValid: true,
      error: null
    };
  }
};

export default pinVerificationService;